import { Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import { action, computed, observable, ObservableMap, runInAction, toJS } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { transl } from '../main/transl';
import { getOmnistore, IOmnistore, useOmnistore } from '../stores/Omnistore';
import { mg } from './mg';

const useStyles = makeStyles(((theme: Theme) => ({
	breadText: {
	},
	breadLink: {
		color: theme.palette.secondary.main,
		cursor: 'pointer',
	},
	breadInfo: {
		// fontStyle: 'italic',//TODO
	},
	breadPage: {
	},
	breadIcon: {
		margin: theme.spacing(0, 1),
		color: theme.palette.grey[500],
		position: 'relative',
		top: 5,
	},
	breadRow: {
	},
})));

interface IBreadcrumbsProps<S extends string> {
	displays: Record<S, (omnistore: IOmnistore, id: any) => string>,
	getVar(key: string) : number|string
	page: string,
	path: Array<string>
	toPageFnMap: Map<string, () => void>
	suffix: Array<string>
}

const Breadcrumbs = observer(function(props: IBreadcrumbsProps<any>) {
	const omnistore = useOmnistore();
	const classes = useStyles();
	const {displays, getVar, page, path, toPageFnMap, suffix} = props;
	function toElement(head: string, tail: string) : JSX.Element | null {
		switch (head) {
		case '^': {
			const onClick = toPageFnMap.get(tail);
			mg.assertPresent(onClick, head, tail);
			return <span className={classes.breadLink} onClick={onClick}>{transl(tail)}</span>;
		}
		case '~': {
			if (tail === 'page') return <span className={classes.breadPage}>{transl(page)}</span>;
			const idOrString = getVar(tail);
			if (idOrString === 0) return null;
			const fun = displays[tail];
			const text = (typeof idOrString === 'string' ? idOrString : fun ? fun(omnistore, idOrString) : null) || idOrString;
			if (!mg.assertPresent(text)) return null;
			return <span className={classes.breadInfo}>{text}</span>;
		}
		case '=': {
			return <span className={classes.breadText}>{transl(tail)}</span>;
		}
		case '/': {
			return <span className={classes.breadText}>{tail}</span>;
		}
		}
		mg.assertPresent(null, head, tail);
		return <span/>;
	}
	const expPath = path.map(s => [s.charAt(0), s.substring(1)]);
	const expSuffix = suffix.map(s => ['/', s]);
	const elements = expPath.concat(expSuffix).map(([h, t]) => toElement(h, t)).filter(mg.isPresent);
	return <Typography variant="h6" color="inherit" noWrap>{
		elements.map((e: JSX.Element, i: number) => <React.Fragment key={i}>
			{i===0 ? null : <ArrowForwardIosIcon className={classes.breadIcon}/>}
			{e}
		</React.Fragment>)
	}</Typography>;
});

export class MgRouter<S extends string> {
	@observable private readonly pages: Record<string, (props: {}) => JSX.Element>;

	private readonly vars: ObservableMap<S, number|string>;
	getVar(key: S) {
		return this.vars.get(key) || 0;
	}
	@action setVar(key: S, value: number|string) {
		this.vars.set(key, value);
	}

	@observable private _page: string;
	get page() {
		return this._page;
	}
	set page(page: string) {
		console.log('page', this._page, page);
		if (page in this.pages) {
			runInAction(() => this._page = page);
		} else {
			console.error('Invalid page', page);
		}
	}

	private readonly toBreadcrumbMap: Map<string, string>;
	private readonly displays: Record<S, (omnistore: IOmnistore, id: number) => string>;

	private readonly toPageFnMap: Map<string, () => void>;

	constructor(
		toBreadcrumbMap: Record<string, string>,
		displays: Record<S, (omnistore: IOmnistore, idOrName: any) => string>,
		pages: Record<string, (props: {}) => JSX.Element>,
	) {
		this.vars = new ObservableMap<S, number>(Object.keys(displays).map(k => [k, 0] as any));
		this.pages = {...pages};
		this.displays = displays;
		this._page = Object.keys(pages)[0];
		this.toPageFnMap = new Map(Object.keys(pages).map((p: string) => ([p, () => this.page = p])));
		this.toBreadcrumbMap = new Map(Object.keys(toBreadcrumbMap).map(k => [k, toBreadcrumbMap[k]]));
	}

	readonly gotoPageFn = (page: string) => {
		return this.toPageFnMap.get(page);
	}

	@computed get component() : (props: {}) => JSX.Element {
		console.log('component', this._page);
		return this.pages[this._page];
	}

	@computed get path() : Array<string> {
		return this.toBreadcrumbMap.get(this.page)?.split(' ') || [];
	}

	@computed get urlHash() {
		const self = this;
		function urlHashFn(s: string) : string {
			if (s.charAt(0) !== '~') return '';
			const key = s.substring(1);
			if (key === 'page') return self.page;
			return key + '=' + self.getVar(key as S);
		}
		return this.path.map(urlHashFn).filter(s => s).join('/');
	}

	@action applyUrl(url: string) {
		console.log('url', url);
		url = url.replace(/.*#!\//, '');
		const query = url.replace(/^.*(?:\?(.*))/, '$1');
		url = url.replace(/\?.*/, '');
		if (url === 'Welcome' && query.match(/^_=/)) this.secret = query.substring(2);
		for (const s of url.split('/').filter(s => s)) {
			const m = s.match(/^(\w+)=(\d+)$/);
			if (!m && s in this.pages) {
				this.page = s;
			} else if (!m) {
				console.error('Unknown URL', '«' + s + '»', url);
			} else if (!this.vars.has(m[1] as S)) {
				console.error('Unknown var in URL', '«' + m[1] + '»', url, JSON.stringify(toJS(this.vars)));
			} else {
				this.setVar(m[1] as S, +m[2]);
			}
		}
	}

	@computed get documentTitle() {
		const self = this;
		const omnistore = getOmnistore();
		function toElement(s: string) {
			const key = s.substring(1);
			switch (s.charAt(0)) {
			case '^': return transl(key);
			case '~': return key==='page' ? transl(self.page) : self.displays[key](omnistore, self.getVar(key as S));
			case '=': return transl(key);
			}
			mg.assertPresent(null, s);
			return '';
		}
		return this.path.map(toElement).join(' » ') + ' — Ordercontrol';
	}

	@computed get breadcrumbs() {
		return <Breadcrumbs
			displays={this.displays}
			getVar={(key: S) => this.getVar(key)}
			page={this.page}
			path={this.path}
			toPageFnMap={this.toPageFnMap}
			suffix={[]}
		/>;
	}

	breadcrumbsOf(varOverrides: Record<S, number|string>, suffix: Array<string>) {
		const getVar = (key: S) => varOverrides[key] ?? this.getVar(key);
		return <Breadcrumbs
			displays={this.displays}
			getVar={getVar}
			page={this.page}
			path={this.path}
			toPageFnMap={this.toPageFnMap}
			suffix={suffix}
		/>;
	}

	@computed get lifecycle() {//TODO lifecycle is unused and broken
		const KEY = 'router';
		const router = this;
		let	shouldPushState = true;
		React.useEffect(() => {
			mg.storage.put(KEY, {...router.vars, page: router.page});
			if (shouldPushState) {
				window.history.pushState('', '', '#!/' + router.urlHash);
			} else {
				shouldPushState = true;
			}
		}, [router.vars, router.page]);
		React.useEffect(() => {
			const listener = () => {
				shouldPushState = false;
				router.applyUrl(window.location.hash);
			};
			window.addEventListener('hashchange', listener, false);
			return () => window.removeEventListener('hashchange', listener);
		}, []);
		return null;
	}

	@observable secret: string = ''; //TODO
	@action clearSecret() {
		this.secret = '';
	}
}
